import React, { useState } from 'react';
import { Button, Popover, message, Typography, Spin, Space } from 'antd';
import { ShareAltOutlined, CopyOutlined, CloseOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';
import { useJourneyStore } from '../store';
import { useCommonInfoStore } from '@src/shared/store';
import { saveShareConfig } from '@src/services/apis/share';
import { TIMEOUT_COEFFICIENTS } from '@src/shared/constants/analysis';

const { Link } = Typography;

const SharePopover = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const customerId = useCommonInfoStore.getState().userInfo.customer_id;

  const {
    applied,

    globalFilter,
    interval,
  } = useJourneyStore();
  const {
    startEvents,
    businessGoal,
    includeEvents,
    excludeEvents,
    hiddenEvents,
    includeMatchType,
    timeout,
    timeoutUnit,
    topPaths,
  } = applied;

  const disabled = !startEvents?.length && !businessGoal;

  const { defaultInterval } = useCommonInfoStore();

  const generateLink = async () => {
    setLoading(true);
    try {
      const payload = {
        filter_config: {
          start_events: startEvents,
          end_event: businessGoal,
          include_events: includeEvents,
          include_events_logic: includeMatchType,
          exclude_events: excludeEvents,
          hidden_events: hiddenEvents,
          metadata: globalFilter || [],
          time_range: interval || defaultInterval,
          timeout_ms: timeout * TIMEOUT_COEFFICIENTS[timeoutUnit],
          limit: topPaths,
          offset: 0,
        },
        description: 'User journey share',
        is_public: false,
      };

      const res = await saveShareConfig(payload);
      const { share_id } = res;
      const { origin } = window.location;
      const link = `${origin}/customer_switch/${customerId}/?next=/app/pattern-analytics/paths/shared/${share_id}`;
      setShareLink(link);
    } catch (error) {
      console.error('Failed to save share config', error);
      message.error('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      void generateLink();
    }
  };

  const handleCopy = () => {
    if (shareLink) {
      copy(shareLink);
      message.info('Share link copied to clipboard');
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const content = (
    <div style={{ width: 300 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              marginBottom: '4px',
              color: 'rgba(0, 0, 0, 0.88)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Share Link</span>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleClose}
              style={{ color: 'rgba(0, 0, 0, 0.45)' }}
            />
          </div>
          <Link
            href={shareLink}
            target="_blank"
            style={{
              wordBreak: 'break-all',
              lineHeight: '1.5',
              display: 'block',
              marginBottom: '8px',
            }}
          >
            {shareLink}
          </Link>
          <Space style={{ display: 'flex', justifyContent: 'end' }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" icon={<CopyOutlined />} onClick={handleCopy} block>
              Copy Link
            </Button>
          </Space>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      arrow={false}
    >
      <Button icon={<ShareAltOutlined />} disabled={disabled} />
    </Popover>
  );
};

export default SharePopover;
